import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import InputPassword from 'creative-ui/uikit/Input/InputPassword';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Flex } from '@/components/Layout/Flex/Flex';
import { PasswordCheck } from '@/components/PasswordCheck/PasswordCheck';

import { useSignupPasswordManager } from '../hooks/useSignupPasswordManager';

interface SignupPasswordFormProps {
  onPasswordSubmit: (password: string) => void;
  fieldErrors: Record<string, string>;
}

interface FormData {
  password: string;
}

function SignupPasswordForm({
  onPasswordSubmit,
  fieldErrors,
}: Readonly<SignupPasswordFormProps>) {
  const { t } = useTranslation('signupPassword');

  const form = useForm<FormData>({
    defaultValues: { password: '' },
    mode: 'onChange',
  });

  const { control, handleSubmit, watch } = form;

  const password = watch('password');

  const { email, username, passwordValidation, texts } =
    useSignupPasswordManager(password);

  const { isValid: passwordIsValid, results: passwordPolicyResults } =
    passwordValidation;

  // SDK first, i18n fallback
  const emailLabel = texts?.emailLabel || t('form.emailLabel');
  const passwordLabel = texts?.passwordLabel || t('form.passwordLabel');
  const passwordPlaceholder =
    texts?.passwordPlaceholder || t('form.passwordPlaceholder');
  const buttonText = texts?.buttonText || t('form.continueButtonText');

  // i18n only
  const passwordCheckHeader = t('form.passwordCheckHeader');
  const passwordRequiredError = t('errors.passwordRequired');

  // SDK errors
  const passwordSDKError = fieldErrors.password;

  const onSubmit = (formData: FormData) => {
    onPasswordSubmit(formData.password);
  };

  const isButtonDisabled = !passwordIsValid;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY="16px">
        {/* Email display (disabled) with Modify link */}
        {(email || username) && (
          <Input name="email" type="email" value={email || username} disabled>
            {emailLabel}
          </Input>
        )}

        {/* Password input with PasswordCheck */}
        <Controller
          name="password"
          control={control}
          rules={{
            required: passwordRequiredError,
          }}
          render={({ field, fieldState }) => (
            <>
              <InputPassword
                {...field}
                placeholder={passwordPlaceholder}
                status={fieldState.error ? 'error' : undefined}
                statusMessage={
                  fieldState.error
                    ? fieldState.error.message || passwordSDKError
                    : undefined
                }>
                {passwordLabel}
              </InputPassword>
              <PasswordCheck
                headerText={passwordCheckHeader}
                passwordComplexityRules={passwordPolicyResults}
              />
            </>
          )}
        />

        <CtaContainer stack>
          <Cta type="primary" submit disabled={isButtonDisabled}>
            {buttonText}
          </Cta>
        </CtaContainer>
      </Flex>
    </form>
  );
}

export default SignupPasswordForm;
